import { ClimateMonths, HotelTile, ImagesResponse, Testimonial } from '@model/iceberg';
import { Country, Destination, Hotel, Image, Region, Resort, TripTypes } from '@model/common';
import { TourDestinationDetails, TourDetails } from '@model/tours';
import { CarouselTile } from '@model/content/carousel';
import { ContentDestination, PageTemplates, TravelGuide, VideoGuide } from './common';

export enum ListType {
  TRAVEL_GUIDE = 'travelGuideList',
  VIDEO_GUIDE = 'videoGuideList'
}
export interface PageData {
  pageType: PageTemplates;
  name: string;
  path: string;
  tripType?: TripTypes;
  destinationPath?: string;
  navPosition?: number;
  listType?: ListType;
}

export interface PageConfig {
  type: PageTemplates;
  name: string;
  components: Array<TemplateComponent>;
}

export interface TemplateData {
  components: Array<TemplateComponent>;
}

export interface Separators {
  above?: string;
  below?: string;
}

export type TemplateComponent =
  | BaseComponent
  | GenericComponent
  | ComplexComponent
  | TitleComponent
  | DescriptionComponent
  | SeoComponent
  | CtaComponent
  | BenefitGroupComponent
  | BreadrumbComponent
  | CarouselComponent
  | ClimateComponent
  | DestinationGroupComponent
  | ExpandableListComponent
  | HeroImageComponent
  | HotelTileGroupComponent
  | ImageComponent
  | PageTileGroupComponent
  | GuidesComponent
  | SeparatorComponent
  | SpecialOffersComponent
  | TabGroupComponent
  | TabComponent
  | TestimonialComponent
  | TourDestinationComponent
  | TourGroupComponent
  | TravelGuideComponent;

export interface BaseComponent {
  handle: string;
  path: string;
  tripType?: TripTypes;
  pageType?: PageTemplates;
  destination?: Destination;
  separators?: Separators;
  data?: any;
  identifier?: string;
}

// Extensible Component types

export interface GenericComponent extends BaseComponent {
  value: string;
}

export interface TextComponent extends GenericComponent {
  alignment?: string;
}

export interface TitleComponent extends BaseComponent {
  title: string;
  subTitle?: string | null;
}

export interface ComplexComponent extends BaseComponent {
  type: ComplexComponentTypes;
}

export interface DescriptionComponent extends TextComponent {
  showReadMore?: boolean;
}

export interface InformationComponent extends TitleComponent {
  description: string;
}

export interface SeoComponent extends InformationComponent {}

export interface CtaComponent extends BaseComponent {
  ctaTitle: string | null;
  ctaUrl?: string;
  hideCta?: boolean;
}

// Components

export enum BenefitGroupType {
  DEFAULT = 'default',
  SIDEBAR = 'sidebar',
  TRUST_BAR = 'trustBar'
}

export interface BenefitGroupComponent extends TitleComponent {
  type: BenefitGroupType;
  benefits: Array<ContentBenefit>;
}

export interface BreadrumbComponent extends BaseComponent {
  breadcrumbs: Array<Country | Region | Resort | Hotel>;
}

export enum CarouselTypes {
  HERO = 'hero',
  BASIC_CENTRE = 'basic-centre'
}
export interface CarouselComponent extends BaseComponent {
  tiles: Array<CarouselTile>;
  type: CarouselTypes;
}

export interface ClimateComponent extends BaseComponent {
  temperatures: ClimateMonths;
  rainfall: ClimateMonths;
  sunshine: ClimateMonths;
  sunrise: null;
  description: string;
}

export interface ContentBenefit extends TitleComponent, CtaComponent {
  details: string;
  icon: string;
}

export enum DestinationGroupTypes {
  STANDARD = 'standard',
  REGION_LIST = 'region-list',
  RESORT_LIST = 'resort-list'
}

export interface DestinationGroupComponent extends TitleComponent, CtaComponent {
  type: DestinationGroupTypes;
  tripTypeOverride: string | null;
  destinations: Array<ContentDestination>;
}

export interface ExpandableListItem extends TitleComponent {
  description: string;
}

export interface ExpandableListComponent extends TitleComponent {
  items: Array<ExpandableListItem>;
}

export interface GridComponent extends ComplexComponent {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  children: JSX.Element | Array<JSX.Element>;
}

export enum HeroImageType {
  STATIC_IMAGE = 'staticImage',
  IMAGE_FROM_PATH = 'imageFromPath'
}

export interface HeroImageComponent extends Omit<BaseComponent, 'destination'>, Pick<ImagesResponse, 'destination'> {
  type: HeroImageType;
  title?: string;
  images: Array<string>;
}

export interface HotelTileGroupComponent extends TitleComponent, Pick<CtaComponent, 'ctaTitle'> {
  tripTypeOverride: string | null;
  hotels: Array<HotelTile>;
  ctaPath: string;
}

export interface ImageComponent extends BaseComponent {
  images: Array<Image>;
}

interface InfoRowGroupItemComponent extends TitleComponent, ExpandableListItem, CtaComponent {}

export interface InfoRowGroupItem extends Omit<InfoRowGroupItemComponent, 'handle' | 'path'> {
  image: Image;
  newWindow: boolean;
}

export interface InfoRowGroupComponent extends InformationComponent {
  items: Array<InfoRowGroupItem>;
}

export interface PageTile {
  icon: string;
  hero: Image;
  title: string;
  shortDescription: string;
  targetPageConfigPath: string;
}

export interface PageTileGroupComponent extends BaseComponent {
  pages: Array<PageTile>;
}

export interface Guide {
  title: string;
  targetPageConfigPath: string;
}

export interface GuidesComponent extends BaseComponent {
  articles: Array<Guide>;
}

export interface SeparatorComponent extends BaseComponent {
  height: number;
}

export interface SpecialOffersComponent extends BaseComponent {
  offers: Array<string>;
}

export enum ComplexComponentTypes {
  OPEN = 'open',
  CLOSE = 'close'
}

export interface TabGroupComponent extends ComplexComponent {}

export interface TabComponent extends TabGroupComponent, TitleComponent {
  icon: string;
}

export interface TestimonialComponent extends BaseComponent {
  testimonials: Array<Testimonial>;
}

export interface TourDestinationComponent extends TitleComponent, CtaComponent {
  packages: Array<TourDestinationDetails>;
}

export enum TourDisplayType {
  GRID = 'grid',
  CAROUSEL = 'carousel'
}

export interface TourGroupComponent extends TitleComponent, CtaComponent {
  packages: Array<TourDetails>;
  displayType: TourDisplayType;
}

export interface TravelGuideComponent extends BaseComponent {
  guides: Array<TravelGuide>;
}

export interface VideoGuideComponent extends BaseComponent {
  guides: Array<VideoGuide>;
}

// Agent components
interface TncentiveTile {
  title: string;
  image: Image;
  shortDescription: string;
  targetPageConfigPath: string;
}

export interface IncentiveTilesComponent extends BaseComponent {
  incentives: Array<TncentiveTile>;
}

export interface DownloadableFile {
  title: string;
  url: string;
}

export interface DownloadableFilesComponent extends BaseComponent {
  dropdownName: string;
  files: Array<DownloadableFile>;
}
